import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { artistInfo } from '../mockData';
import { seriesList } from '../data/seriesData';

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [workDropdownOpen, setWorkDropdownOpen] = useState(false);
  const [mobileWorkOpen, setMobileWorkOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setWorkDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const goToSection = (id) => {
    setMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate(`/#${id}`);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goToSeries = (slug) => {
    setWorkDropdownOpen(false);
    setMobileMenuOpen(false);
    setMobileWorkOpen(false);
    navigate(`/series/${slug}`);
    window.scrollTo(0, 0);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-neutral-900/95 backdrop-blur-sm border-b border-neutral-800">
      <nav className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-home-logo"
            className="text-2xl font-bold text-neutral-50 tracking-tight hover:text-red-500 transition-colors"
          >
            {artistInfo.name}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {/* Work Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                data-testid="nav-work-dropdown"
                onClick={() => setWorkDropdownOpen(!workDropdownOpen)}
                className="text-neutral-300 hover:text-neutral-50 transition-colors flex items-center gap-1"
              >
                Work
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${workDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {workDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-72 bg-neutral-900 border border-neutral-700 rounded-lg shadow-2xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-neutral-800 bg-neutral-950">
                    <p className="text-xs uppercase tracking-wider text-neutral-500 font-medium">
                      Bodies of Work
                    </p>
                  </div>
                  {seriesList.map((series) => (
                    <button
                      key={series.slug}
                      data-testid={`nav-series-${series.slug}`}
                      onClick={() => goToSeries(series.slug)}
                      className="w-full text-left px-4 py-3 text-neutral-300 hover:bg-neutral-800 hover:text-red-500 transition-colors border-b border-neutral-800 last:border-b-0 flex items-baseline gap-3"
                    >
                      <span className="text-xs text-neutral-600 font-mono">
                        {String(series.order).padStart(2, '0')}
                      </span>
                      <span className="flex-1">{series.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              data-testid="nav-about"
              onClick={() => goToSection('about')}
              className="text-neutral-300 hover:text-neutral-50 transition-colors"
            >
              About
            </button>
            <button
              data-testid="nav-exhibitions"
              onClick={() => goToSection('exhibitions')}
              className="text-neutral-300 hover:text-neutral-50 transition-colors"
            >
              Exhibitions
            </button>
            <button
              data-testid="nav-contact"
              onClick={() => goToSection('contact')}
              className="text-neutral-300 hover:text-neutral-50 transition-colors"
            >
              Contact
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden text-neutral-50"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 flex flex-col gap-2">
            <button
              onClick={() => setMobileWorkOpen(!mobileWorkOpen)}
              className="text-neutral-300 hover:text-neutral-50 transition-colors text-left py-2 flex items-center justify-between"
            >
              <span>Work</span>
              <ChevronDown
                size={16}
                className={`transition-transform duration-200 ${mobileWorkOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {mobileWorkOpen && (
              <div className="pl-4 flex flex-col gap-1 border-l-2 border-red-600 mb-2">
                {seriesList.map((series) => (
                  <button
                    key={series.slug}
                    onClick={() => goToSeries(series.slug)}
                    className="text-neutral-400 hover:text-red-500 transition-colors text-left py-2 text-sm"
                  >
                    {String(series.order).padStart(2, '0')}. {series.title}
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={() => goToSection('about')}
              className="text-neutral-300 hover:text-neutral-50 transition-colors text-left py-2"
            >
              About
            </button>
            <button
              onClick={() => goToSection('exhibitions')}
              className="text-neutral-300 hover:text-neutral-50 transition-colors text-left py-2"
            >
              Exhibitions
            </button>
            <button
              onClick={() => goToSection('contact')}
              className="text-neutral-300 hover:text-neutral-50 transition-colors text-left py-2"
            >
              Contact
            </button>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navigation;
